import { FC, useState, ReactElement, useImperativeHandle, forwardRef } from 'react';
import { useTrans } from 'base/utils/i18n';
import { Image } from '@base/lib/ThemeImage';
import cn from 'classnames';
import styles from './index.module.less';
import { Drawer } from '@base/lib/Drawer';
import { Link } from '@base/lib/ThemeLink';
import Iconfont from '@base/lib/Iconfont';
import { Message } from '@base/lib/Message';
import { ISharePopupInstance, useSharePopup } from '@base/lib/share/useSharePopup';
import { useStoreState, observer, useStore } from 'base/store';
import { INativeShareData, nativeLogin, nativeShare } from 'base/utils/app';
import { IUtmQuery } from 'base/utils/request';
import { getSharePopupTrackData, sharePopupModuleCode } from '@base/lib/share/utils';
import Tracker from '@base/lib/Tracker';
import { displayCtr, trackElement } from 'base/utils/tracker';
import { SHARE_CHANNELS_CONFIG } from '@base/lib/share/constant';
import { IChannelType, IClickShareItem, IGivenChannelType } from '@base/lib/share/interface';
import { Spin } from 'antd';
import { useRouter } from 'next/router';
import { RouteEnum } from 'pro/config/routers';
import { generatePath } from 'ava-util/dist/request';

type IRenderProps = {
    open: () => void;
}

type IShareRef = {
    onClose: () => void;
}

export type ISharePopupMProps = {
    className?: string;
    url?: string;
    shareUrls?: ISharePopupInstance;
    title?: string;
    media?: string;
    showThumb?: boolean;
    children: (props: IRenderProps) => ReactElement;
    utmQuery?: IUtmQuery;
    trackTargetPrefix?: string;
    onClickItem?: (shareChannel?: IClickShareItem) => void;
    needLogin?: boolean;
};

type IPopupContentProps = ISharePopupMProps & {
    onClose: () => void;
}

type IShareChannelList = {
    className?: string;
    trackTargetPrefix?: string;
    shareUrls?: ISharePopupInstance;
    onClickItem?: (shareChannel?: IClickShareItem) => void;
    showName?: boolean;
    onClose: () => void;
}

export const ShareChannelList: FC<IShareChannelList> = (props) => {
    const {
        className = '',
        trackTargetPrefix,
        shareUrls,
        onClickItem,
        showName = true,
        onClose,
    } = props

    const { t } = useTrans();

    return (
        <div className={className}>
            <div className="g-list g-sns-list flex">
                {(['facebook', 'x', 'whatsapp'] as IGivenChannelType[]).map((channel) => {
                    const shareChannel = SHARE_CHANNELS_CONFIG.find(
                        (channelConfig) => channelConfig.channel === channel
                    )
                    if (!shareChannel) {
                        return ''
                    }
                    return (
                        <Tracker
                            key={channel}
                            trigger={'click'}
                            data={getSharePopupTrackData(shareChannel.track, trackTargetPrefix)}
                        >
                            <div className={'g-item'} onClick={() => {
                                onClickItem?.({
                                    channel: channel,
                                })
                                onClose();
                            }}>
                                <Link href={shareUrls?.[`${channel}Url`] || ''}>
                                    <a target={'_blank'}>
                                        <Image
                                            className={'g-sns-icon'}
                                            src={shareChannel.imageUrl}
                                            width={42}
                                            fixed
                                            background={false}
                                            pure
                                        />
                                        {/*<span className={'g-title'}>{t('web_common_share_facebook_title')}</span>*/}
                                        {showName && (
                                            <span className={'g-title'}>{
                                                (shareChannel.langCode && t(shareChannel.langCode)) ||
                                                shareChannel.text
                                            }</span>
                                        )}

                                    </a>
                                </Link>
                            </div>
                        </Tracker>
                    )
                })
                }

            </div>
        </div>
    )
}

export const PopupContent: FC<IPopupContentProps> = observer(props => {
    const {
        media,
        showThumb,
        title,
        trackTargetPrefix,
        onClickItem,
        shareUrls,
        onClose,
    } = props;

    const { t } = useTrans();

    const { shortUrlsObj, urlsObj, isLoading } = shareUrls || {}
    const targetUrl = shortUrlsObj?.other || urlsObj?.other || ''

    const handleCopy = () => {
        navigator.clipboard.writeText(targetUrl).then(() => {
            Message.success(t('web_common_link_copied_tip'));
        }).catch(e => {
            Message.error(e.message);
        });
        onClose();
        onClickItem?.({
            channel: 'copyLink'
        })
    };

    const handleMore = () => {
        if (navigator.share) {
            navigator.share({
                url: targetUrl,
                // title,
                text: title,
            })
            onClose();
            onClickItem?.({
                channel: 'copyLink'
            })
        } else {
            //当浏览器不支持此功能时
            Message.error(t('web_common_share_unsupport_tip'));
        }
    };

    return (
        <div data-t-mod={sharePopupModuleCode}>
            {showThumb && (
                <div className="g-thumb flex">
                    {media && <Image src={media} width={60} fixed/>}
                    <div className={'flex flex-1 flex-vertical flex-justify'}>
                        <div className={'g-name'}>
                            {title}
                        </div>
                    </div>

                </div>
            )}

            <Spin spinning={isLoading}>

                <ShareChannelList
                    onClickItem={onClickItem}
                    onClose={onClose}
                    shareUrls={shareUrls}
                    trackTargetPrefix={trackTargetPrefix}
                />

                <div className="g-list flex">
                    <Tracker
                        trigger={'click'}
                        data={getSharePopupTrackData('CopyLink', trackTargetPrefix)}
                    >
                        <div className={'g-item'} onClick={handleCopy}>
                            <div className="g-icon g-other-icon">
                                <Iconfont className={'icon-link'}/>
                            </div>
                            <p className={'g-title'}>{t('web_common_share_copy_link')}</p>
                        </div>
                    </Tracker>

                    <Tracker
                        trigger={'click'}
                        data={getSharePopupTrackData('More', trackTargetPrefix)}
                    >
                        <div className={'g-item'} onClick={handleMore}>
                            <div className="g-icon g-other-icon">
                                <Iconfont className={'icon-more-horizontal'}/>
                            </div>
                            <p className={'g-title'}>{t('web_common_more')}</p>
                        </div>
                    </Tracker>
                </div>
            </Spin>
        </div>
    )
});

export const SharePopupM = observer(forwardRef<IShareRef, ISharePopupMProps>((props, ref) => {
    const {
        url,
        title,
        media,
        trackTargetPrefix,
        className,
        children,
        onClickItem,
        shareUrls,
        utmQuery,
        needLogin = true,
    } = props;

    const router = useRouter();
    const { userStore } = useStore();

    const [visible, setVisible] = useState<boolean>(false);
    const { ua: { isApp } } = useStoreState('rootStore');
    const { t } = useTrans();

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const _shareUrls = shareUrls || useSharePopup({ url, utmQuery });

    const handleOpen = () => {
        if (needLogin && !userStore.checkAuth()) {
            if (isApp) {
                nativeLogin()
            } else {
                router.push(generatePath(RouteEnum.LOGIN, { redirect: router.asPath }))
            }
            return
        }

        if (isApp) {
            const data: INativeShareData = {
                links: _shareUrls.shortUrlsArray || Object.keys(_shareUrls.urlsObj).map((channelKey) => {
                    return {
                        type: channelKey as IChannelType,
                        link: _shareUrls.urlsObj[channelKey]
                    }
                })
            };
            if (title) {
                data.text = title;
            }
            if (media) {
                data.images = [media]
            }
            nativeShare(data, () => onClickItem?.());
        } else {
            setVisible(true)

            trackElement({
                action: 'impr',
                ...getSharePopupTrackData('SharePopup', trackTargetPrefix)
            });
            displayCtr(sharePopupModuleCode);
        }
    };

    useImperativeHandle(ref, () => ({
        onClose: () => setVisible(false)
    }));

    return (
        <>
            {children?.({
                open: !_shareUrls.isLoading ? handleOpen : () => {
                }
            })}

            {!isApp && (
                <Drawer
                    className={cn(className, styles['share-m'])}
                    open={visible}
                    onOpenChange={setVisible}
                    title={t('web_common_share_popup_title')}
                    body={
                        <PopupContent
                            {...props}
                            shareUrls={_shareUrls}
                            onClose={() => setVisible(false)}
                        />
                    }
                />
            )}
        </>
    )
}));
